import { useEffect } from "react";

const SLIDE_SELECTOR = ".slick-slide";

function supportsInert() {
	return typeof HTMLElement !== "undefined" && "inert" in HTMLElement.prototype;
}

/**
 * When infinite mode clones slides, Slick sets aria-hidden="true" on off-screen
 * slides but leaves focusable descendants tabbable. Lighthouse flags this.
 */
function applyTabindexFallback() {
	document.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
		const hidden = slide.getAttribute("aria-hidden") === "true";
		slide
			.querySelectorAll(
				'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])',
			)
			.forEach((el) => {
				if (hidden) {
					if (!el.hasAttribute("data-sb-tabindex-saved")) {
						const ti = el.getAttribute("tabindex");
						el.setAttribute(
							"data-sb-tabindex-saved",
							ti === null ? "__NONE__" : ti,
						);
					}
					el.setAttribute("tabindex", "-1");
				} else {
					const saved = el.getAttribute("data-sb-tabindex-saved");
					if (saved !== null) {
						if (saved === "__NONE__") {
							el.removeAttribute("tabindex");
						} else {
							el.setAttribute("tabindex", saved);
						}
						el.removeAttribute("data-sb-tabindex-saved");
					}
				}
			});
	});
}

function syncSlickSlideAccessibility() {
	document.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
		const hidden = slide.getAttribute("aria-hidden") === "true";
		if (supportsInert()) {
			slide.inert = hidden;
		}
	});
	if (!supportsInert()) {
		applyTabindexFallback();
	}
}

/**
 * Observes the document for Slick carousel slide visibility changes and keeps
 * aria-hidden clones out of the tab order (inert or tabindex fallback).
 */
const SlickAccessibilityFix = () => {
	useEffect(() => {
		syncSlickSlideAccessibility();
		let raf = 0;
		const schedule = () => {
			if (raf) {
				cancelAnimationFrame(raf);
			}
			raf = requestAnimationFrame(() => {
				raf = 0;
				syncSlickSlideAccessibility();
			});
		};
		const observer = new MutationObserver(schedule);
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ["aria-hidden"],
			subtree: true,
			childList: true,
		});
		return () => {
			observer.disconnect();
			if (raf) {
				cancelAnimationFrame(raf);
			}
		};
	}, []);
	return null;
};

export default SlickAccessibilityFix;
